import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  Application, Graphics, TilingSprite, Sprite,
  Texture, BaseTexture,
} from 'pixi.js'
import lottie, { AnimationItem } from 'lottie-web'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'
import { fmtCoins } from '../../lib/cards'
import { io, Socket } from 'socket.io-client'
import { Sounds } from '../../lib/sounds'

const A = {
  biker:    '/indian-road/biker.json',
  obstacle: '/indian-road/obstacle.json',
  pothole:  '/indian-road/pothole.jpg',
  road:     '/indian-road/road.svg',
  bridge:   '/indian-road/bridge.svg',
  milestone:'/indian-road/milestone.svg',
}

const SEG_RIDE:  [number,number] = [0, 59]
const SEG_CRASH: [number,number] = [79, 121]

const CANVAS_H        = 430
const SKY_H           = 180
const ROAD_TOP_Y      = SKY_H
const ROAD_H          = 145
const ROAD_BTM_Y      = ROAD_TOP_Y + ROAD_H
const ROAD_MID_Y      = Math.round((ROAD_TOP_Y + ROAD_BTM_Y) / 2)
const BIKER_X         = 155
const BIKER_W         = 200
const BIKER_H         = Math.round(BIKER_W * 388/543)
const OBS_W           = 110
const OBS_H           = 110
const POTHOLE_W       = 160
const POTHOLE_H       = Math.round(POTHOLE_W * 2850/7721)
const HOP_HEIGHT      = 95
const HOP_MS          = 700
const ROAD_SPEED_BASE = 3.5
const JUMP_ZONE_LEFT  = BIKER_X - 30
const JUMP_ZONE_RIGHT = BIKER_X + 320

const DIFF: Record<string,{label:string;color:string;survival:string}> = {
  easy:     {label:'Easy',    color:'#22c55e',survival:'91%'},
  medium:   {label:'Medium',  color:'#f59e0b',survival:'82%'},
  hard:     {label:'Hard',    color:'#ef4444',survival:'70%'},
  hardcore: {label:'Hardcore',color:'#7c3aed',survival:'55%'},
}

interface Props {
  token:string; balance:number|null
  playerId?:string; operatorId?:string; displayName?:string
}
type Phase = 'idle'|'starting'|'riding'|'cooked'|'finished'
interface PotholeObj { sprite:Sprite; vx:number; id:number; hopSent:boolean }

const easeOut = (t:number) => 1-(1-t)**2
const easeIn  = (t:number) => t*t

async function loadPotholeJPG(url:string): Promise<Texture|null> {
  return new Promise(resolve=>{
    const img=new Image(); img.crossOrigin='anonymous'
    img.onload=()=>{
      const c=document.createElement('canvas')
      c.width=POTHOLE_W*2; c.height=POTHOLE_H*2
      const ctx=c.getContext('2d')!
      ctx.drawImage(img,0,0,c.width,c.height)
      const id=ctx.getImageData(0,0,c.width,c.height); const d=id.data
      for(let i=0;i<d.length;i+=4) if(d[i]>230&&d[i+1]>220&&d[i+2]>200) d[i+3]=0
      ctx.putImageData(id,0,0)
      resolve(new Texture(new BaseTexture(c)))
    }
    img.onerror=()=>resolve(null); img.src=url
  })
}

async function loadSVGTexture(url:string,w:number,h:number): Promise<Texture|null> {
  return new Promise(resolve=>{
    const img=new Image(); img.crossOrigin='anonymous'
    img.onload=()=>{
      const c=document.createElement('canvas'); c.width=w; c.height=h
      c.getContext('2d')!.drawImage(img,0,0,w,h)
      resolve(new Texture(new BaseTexture(c)))
    }
    img.onerror=()=>resolve(null); img.src=url
  })
}

function spawnCrashParticles(app:Application) {
  const cx=BIKER_X, cy=ROAD_MID_Y, parts:any[]=[]
  for(let i=0;i<18;i++){
    const g=new Graphics() as any
    g.beginFill([0xFF4400,0xFFAA00,0xFF8800,0xFFDD00,0xCC2200][i%5])
    g.drawCircle(0,0,3+Math.random()*7); g.endFill()
    g.x=cx+(Math.random()-.5)*30; g.y=cy+(Math.random()-.5)*20
    const a=(i/18)*Math.PI*2, sp=3+Math.random()*7
    g.vx=Math.cos(a)*sp; g.vy=Math.sin(a)*sp-4; g.grav=0.35
    app.stage.addChild(g); parts.push(g)
  }
  for(let i=0;i<6;i++){
    const g=new Graphics() as any
    g.beginFill(0x333333); g.drawRect(-4,-2,10,5); g.endFill()
    g.x=cx+(Math.random()-.5)*20; g.y=cy+(Math.random()-.5)*10
    g.rotation=Math.random()*Math.PI
    const a=(Math.random()-.5)*Math.PI, sp=2+Math.random()*4
    g.vx=Math.cos(a)*sp; g.vy=Math.sin(a)*sp-2; g.grav=0.4
    app.stage.addChild(g); parts.push(g)
  }
  let fr=0
  const animate=()=>{
    fr++
    parts.forEach(p=>{
      if(p.alpha<=0){p.parent?.removeChild(p);return}
      p.vy+=p.grav; p.x+=p.vx; p.y+=p.vy; p.alpha-=0.025; p.rotation+=0.08
    })
    if(fr<80) requestAnimationFrame(animate)
    else parts.forEach(p=>p.parent?.removeChild(p))
  }
  requestAnimationFrame(animate)
}

export function IndianRoadPixi({token,balance:initBalance,playerId:initPid,operatorId:initOid,displayName}:Props){
  const containerRef = useRef<HTMLDivElement>(null)
  const bikerRef     = useRef<HTMLDivElement>(null)
  const obsRef       = useRef<HTMLDivElement>(null)
  const appRef       = useRef<Application|null>(null)
  const socketRef    = useRef<Socket|null>(null)
  const lottieRef    = useRef<AnimationItem|null>(null)
  const obsLottieRef = useRef<AnimationItem|null>(null)
  const obsRAF       = useRef(0)
  const obsXRef      = useRef(9999)
  const sessionRef   = useRef({playerId:initPid||'',operatorId:initOid||''})

  const scene = useRef({
    road:       null as TilingSprite|null,
    potholes:   [] as PotholeObj[],
    potholeTex: null as Texture|null,
    roadSpeed:  ROAD_SPEED_BASE,
    riding:     false,
    jumping:    false,
    hopRAF:     0,
    spawnTimer: 0 as any,
    obsTimer:   0 as any,
    potholeId:  0,
    stepCount:  0,
  })

  const [phase,setPhase]           = useState<Phase>('idle')
  const [balance,setBalance]       = useState<number|null>(initBalance)
  const [multiplier,setMultiplier] = useState(0)
  const [step,setStep]             = useState(0)
  const [stake,setStake]           = useState(10)
  const [difficulty,setDifficulty] = useState('easy')
  const [toast,setToast]           = useState('')
  const [bikerY,setBikerY]         = useState(0)
  const [bikerTilt,setBikerTilt]   = useState(0)
  const [crashed,setCrashed]       = useState(false)
  const [potholeNear,setPotholeNear] = useState(false)
  const [assetsReady,setAssetsReady] = useState(false)
  const [jumpCooldown,setJumpCooldown] = useState(false)
  const [obsX,setObsX]             = useState(9999)
  const [obsVisible,setObsVisible] = useState(false)
  const [flashRed,setFlashRed]     = useState(false)
  const [currency,setCurrency]     = useState('coins')

  useEffect(()=>{ if(initBalance!==null) setBalance(initBalance) },[initBalance])

  const showToast=(msg:string,dur=2500)=>{setToast(msg);setTimeout(()=>setToast(''),dur)}

  const lottieRide=useCallback(()=>{
    const a=lottieRef.current; if(!a) return
    let forward=true
    a.loop=false; a.setDirection(1); a.playSegments(SEG_RIDE,true)
    a.addEventListener('complete',function pingPong(){
      forward=!forward; a.setDirection(forward?1:-1); a.play()
    })
  },[])

  const lottieCrash=useCallback(()=>{
    const a=lottieRef.current; if(!a) return
    a.loop=false; a.playSegments(SEG_CRASH,true)
  },[])

  const lottieStop=useCallback(()=>{
    const a=lottieRef.current; if(!a) return
    a.stop(); a.goToAndStop(0,true)
  },[])

  const doHop=useCallback(()=>{
    const sc=scene.current; if(sc.jumping) return
    sc.jumping=true; cancelAnimationFrame(sc.hopRAF)
    const t0=performance.now(); const half=HOP_MS/2
    const tick=(now:number)=>{
      const el=now-t0; let y:number
      if(el<half)        y=-HOP_HEIGHT*easeOut(el/half)
      else if(el<HOP_MS) y=-HOP_HEIGHT*(1-easeIn((el-half)/half))
      else{ setBikerY(0); sc.jumping=false; return }
      setBikerY(y); sc.hopRAF=requestAnimationFrame(tick)
    }
    sc.hopRAF=requestAnimationFrame(tick)
  },[])

  const triggerCrash=useCallback(()=>{
    const sc=scene.current
    sc.riding=false
    clearTimeout(sc.spawnTimer); clearTimeout(sc.obsTimer)
    cancelAnimationFrame(sc.hopRAF); sc.jumping=false
    cancelAnimationFrame(obsRAF.current); setObsVisible(false)
    setFlashRed(true); setTimeout(()=>setFlashRed(false),400)
    setCrashed(true); setBikerTilt(35)
    Sounds.roadCrash()
    lottieCrash()
    if(appRef.current) spawnCrashParticles(appRef.current)
    sc.potholes.forEach(p=>p.sprite.parent?.removeChild(p.sprite))
    sc.potholes=[]
    setPhase('cooked')
    showToast('💥 Crashed!',3000)
    setTimeout(()=>{
      setCrashed(false); setBikerTilt(0); setBikerY(0)
      setPhase('idle'); lottieStop()
    },3500)
  },[lottieCrash,lottieStop])

  const spawnObstacle=useCallback((canvasW:number)=>{
    const sc=scene.current; if(!sc.riding) return
    cancelAnimationFrame(obsRAF.current)
    obsXRef.current=canvasW+120
    setObsX(obsXRef.current); setObsVisible(true)
    obsLottieRef.current?.play()
    Sounds.roadScooterWarn()
    const speed=sc.roadSpeed*1.1
    const move=()=>{
      obsXRef.current-=speed; setObsX(obsXRef.current)
      if(obsXRef.current<BIKER_X+55&&obsXRef.current>BIKER_X-55){
        setObsVisible(false); obsLottieRef.current?.stop()
        if(scene.current.riding) triggerCrash()
        return
      }
      if(obsXRef.current<-200){ setObsVisible(false); obsLottieRef.current?.stop(); return }
      obsRAF.current=requestAnimationFrame(move)
    }
    obsRAF.current=requestAnimationFrame(move)
  },[triggerCrash])

  const scheduleObstacle=useCallback((canvasW:number)=>{
    const sc=scene.current; if(!sc.riding) return
    const delay=14000+Math.random()*16000
    sc.obsTimer=setTimeout(()=>{
      if(!scene.current.riding) return
      spawnObstacle(canvasW); scheduleObstacle(canvasW)
    },delay)
  },[spawnObstacle])

  const spawnPothole=useCallback(()=>{
    const sc=scene.current; const app=appRef.current
    if(!app||!sc.potholeTex||!sc.riding) return
    const sprite=new Sprite(sc.potholeTex)
    sprite.anchor.set(0.5,0.5)
    sprite.width=POTHOLE_W; sprite.height=POTHOLE_H
    sprite.x=(app.view as HTMLCanvasElement).width+60
    sprite.y=ROAD_MID_Y
    app.stage.addChild(sprite)
    sc.potholes.push({sprite,vx:-(sc.roadSpeed+1),id:++sc.potholeId,hopSent:false})
  },[])

  const scheduleNextPothole=useCallback((stepCount:number)=>{
    const sc=scene.current; if(!sc.riding) return
    const minMs=Math.max(1000,2200-stepCount*80)
    const maxMs=Math.max(2000,4000-stepCount*100)
    const delay=minMs+Math.random()*(maxMs-minMs)
    sc.spawnTimer=setTimeout(()=>{ spawnPothole(); scheduleNextPothole(sc.stepCount) },delay)
  },[spawnPothole])

  useEffect(()=>{
    const GS=(import.meta as any).env?.VITE_GAME_SERVER_URL||'http://localhost:3002'
    const s=io(GS,{transports:['polling'],auth:{token}})
    socketRef.current=s
    try{
      const p=JSON.parse(atob(token.split('.')[1]))
      sessionRef.current={playerId:p.sub||'',operatorId:p.operator_id||''}
      if(p.currency) setCurrency(p.currency)
    }catch{}
    s.on('connect',async()=>{
      try{
        const {playerId}=sessionRef.current
        const r=await fetch(`${GS}/wallet/balance?player_id=${playerId}`)
        const d=await r.json()
        if(typeof d.balance==='number') setBalance(d.balance)
      }catch{}
    })
    s.on('chicken:start_result',(d:any)=>{
      if(!d.success){showToast(`❌ ${d.error||'Failed'}`);setPhase('idle');return}
      scene.current.riding=true; scene.current.roadSpeed=ROAD_SPEED_BASE; scene.current.stepCount=0
      setStep(0); setMultiplier(0); setPhase('riding')
      setBalance(b=>b!==null?Math.max(0,b-stake):b)
      Sounds.roadStart()
      lottieRide(); scheduleNextPothole(0)
      const canvasW=(appRef.current?.view as HTMLCanvasElement)?.width||420
      scheduleObstacle(canvasW)
    })
    s.on('chicken:hop_result',(d:any)=>{
      if(d.safe===false){ triggerCrash() }
      else {
        const ns=d.step||0; const nm=d.multiplier||0
        setStep(ns); setMultiplier(nm)
        scene.current.roadSpeed=ROAD_SPEED_BASE+ns*0.25
        scene.current.stepCount=ns
        Sounds.roadSafe()
        showToast(`✅ ${nm.toFixed(2)}×`,1000)
      }
    })
    s.on('chicken:cashout_result',(d:any)=>{
      if(!d.success){showToast(`❌ ${d.error||'Failed'}`);return}
      const payout=d.payout||0
      clearTimeout(scene.current.spawnTimer); clearTimeout(scene.current.obsTimer)
      cancelAnimationFrame(obsRAF.current); setObsVisible(false)
      scene.current.riding=false
      scene.current.potholes.forEach(p=>p.sprite.parent?.removeChild(p.sprite))
      scene.current.potholes=[]
      setPhase('finished')
      Sounds.roadCashout()
      setBalance(b=>b!==null?b+payout:payout)
      showToast(`🏆 +₹${payout.toLocaleString()}!`,3000)
      setTimeout(()=>{setPhase('idle');lottieStop()},3200)
    })
    s.on('balance',(d:any)=>{if(typeof d.balance==='number')setBalance(d.balance)})
    return ()=>{ s.disconnect() }
  },[token,stake,lottieRide,triggerCrash,scheduleNextPothole,scheduleObstacle,lottieStop])

  useEffect(()=>{
    const iv=setInterval(()=>{
      const sc=scene.current
      if(!sc.riding){setPotholeNear(false);return}
      setPotholeNear(sc.potholes.some(p=>p.sprite.x>JUMP_ZONE_LEFT&&p.sprite.x<JUMP_ZONE_RIGHT))
    },50)
    return ()=>clearInterval(iv)
  },[])

  useEffect(()=>{
    const onKey=(e:KeyboardEvent)=>{
      if(e.code==='Space'||e.code==='ArrowUp'){e.preventDefault();handleJump()}
    }
    window.addEventListener('keydown',onKey)
    return ()=>window.removeEventListener('keydown',onKey)
  })

  // PixiJS setup — named imports only, identical pattern to ChickenRoadPixi
  useEffect(()=>{
    const container=containerRef.current; if(!container) return
    const W=container.clientWidth||420
    const app = new Application({
      width:W, height:CANVAS_H, backgroundColor:0x0d0b07,
      antialias:true, resolution:window.devicePixelRatio||1, autoDensity:true,
    })
    appRef.current = app
    container.appendChild(app.view as HTMLCanvasElement)
    // app.ticker.maxFPS = 30 — removed, runs at full 60fps

    const sky=new Graphics()
    ;[0xFF5722,0xFF7043,0xFF8A50,0xFFA970,0xFFCC99].forEach((c,i,arr)=>{
      sky.beginFill(c);sky.drawRect(0,i*(SKY_H/arr.length),W,SKY_H/arr.length+1);sky.endFill()
    })
    app.stage.addChild(sky)
    const gnd=new Graphics()
    gnd.beginFill(0x5C3D1A);gnd.drawRect(0,ROAD_BTM_Y,W,CANVAS_H-ROAD_BTM_Y);gnd.endFill()
    app.stage.addChild(gnd)

    Promise.all([
      loadSVGTexture(A.road,800,160),
      loadPotholeJPG(A.pothole),
      loadSVGTexture(A.bridge,500,220),
      loadSVGTexture(A.milestone,80,130),
    ]).then(([roadTex,potholeTex,bridgeTex,milestoneTex])=>{
      const sc=scene.current
      if(roadTex){
        const road=new TilingSprite(roadTex,W,ROAD_H)
        road.y=ROAD_TOP_Y; road.tileScale.set(ROAD_H/160)
        app.stage.addChild(road); sc.road=road
      }
      if(bridgeTex){
        const br=new Sprite(bridgeTex)
        br.width=400;br.height=Math.round(400*220/500)
        br.x=W-420;br.y=ROAD_TOP_Y-br.height+20;br.alpha=0.6
        app.stage.addChild(br)
      }
      sc.potholeTex=potholeTex
      if(milestoneTex){
        const ms=new Sprite(milestoneTex)
        ms.anchor.set(0,1);ms.width=52;ms.height=Math.round(52*130/80)
        ms.x=14;ms.y=ROAD_BTM_Y
        app.stage.addChild(ms)
      }
      setAssetsReady(true)
      app.ticker.add(()=>{
        if(sc.road&&sc.riding) sc.road.tilePosition.x-=sc.roadSpeed
        sc.potholes=sc.potholes.filter(p=>{
          p.sprite.x+=p.vx
          if(!p.hopSent&&p.sprite.x<BIKER_X-POTHOLE_W/2-10){
            p.sprite.parent?.removeChild(p.sprite)
            if(sc.riding) triggerCrash()
            return false
          }
          if(p.sprite.x<-POTHOLE_W-20){p.sprite.parent?.removeChild(p.sprite);return false}
          return true
        })
      })
    })
    return ()=>{
      clearTimeout(scene.current.spawnTimer); clearTimeout(scene.current.obsTimer)
      cancelAnimationFrame(scene.current.hopRAF); cancelAnimationFrame(obsRAF.current)
      app.destroy(true,{children:true,texture:true,baseTexture:true})
      appRef.current=null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  useEffect(()=>{
    if(!bikerRef.current) return
    const anim=lottie.loadAnimation({
      container:bikerRef.current,renderer:'svg',
      loop:true,autoplay:false,path:A.biker,initialSegment:SEG_RIDE,
    })
    lottieRef.current=anim
    anim.goToAndStop(0,true)
    return ()=>{anim.destroy();lottieRef.current=null}
  },[])

  useEffect(()=>{
    if(!obsRef.current) return
    const anim=lottie.loadAnimation({
      container:obsRef.current,renderer:'svg',
      loop:true,autoplay:false,path:A.obstacle,
    })
    obsLottieRef.current=anim
    return ()=>{anim.destroy();obsLottieRef.current=null}
  },[])

  const handleJump=useCallback(()=>{
    const sc=scene.current
    const s=socketRef.current
    const {playerId,operatorId}=sessionRef.current
    if(!sc.riding||jumpCooldown||sc.jumping) return
    const activePothole=sc.potholes.find(p=>
      !p.hopSent&&p.sprite.x>=JUMP_ZONE_LEFT&&p.sprite.x<=JUMP_ZONE_RIGHT
    )
    doHop()
    Sounds.roadJump()
    setJumpCooldown(true); setTimeout(()=>setJumpCooldown(false),550)
    if(activePothole&&s){
      activePothole.hopSent=true
      s.emit('chicken:hop',{player_id:playerId,operator_id:operatorId})
    }
  },[doHop,jumpCooldown])

  const handleCashout=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||phase!=='riding'||step===0) return
    s.emit('chicken:cashout',{player_id:playerId,operator_id:operatorId})
  }

  const AMOUNTS    = [5,10,25,50,100,500]
  const cashoutVal = step>0?Math.floor(stake*multiplier*100)/100:0
  const bikerTop   = Math.round(ROAD_MID_Y - BIKER_H*0.72)

  return(
    <div style={{position:'relative',width:'100%',height:CANVAS_H,overflow:'hidden',
      fontFamily:'system-ui,sans-serif',userSelect:'none',background:'#0d0b07'}}>
      <style>{`
        @keyframes irToast{0%{opacity:0;transform:translateX(-50%) translateY(-8px)}15%,85%{opacity:1;transform:translateX(-50%) translateY(0)}100%{opacity:0}}
        @keyframes irDanger{0%,100%{transform:translateX(-50%) scale(1)}50%{transform:translateX(-50%) scale(1.1)}}
        @keyframes irPulse{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.8)}50%{box-shadow:0 0 0 16px rgba(239,68,68,0)}}
        @keyframes irShake{0%,100%{transform:translateX(0) rotate(var(--tilt))}20%{transform:translateX(-5px) rotate(var(--tilt))}40%{transform:translateX(5px) rotate(var(--tilt))}60%{transform:translateX(-4px) rotate(var(--tilt))}80%{transform:translateX(4px) rotate(var(--tilt))}}
        .ir-jump{animation:irPulse .7s infinite;}
        .ir-chip{transition:all .12s;cursor:pointer;}
        .ir-chip:hover{transform:scale(1.1);}
        .ir-diff{transition:all .15s;cursor:pointer;}
      `}</style>

      <div ref={containerRef} style={{position:'absolute',inset:0}}/>

      <div style={{position:'absolute',inset:0,zIndex:15,pointerEvents:'none',
        background:'rgba(220,30,30,.5)',opacity:flashRed?1:0,transition:'opacity .15s ease-out'}}/>

      <div ref={bikerRef} style={{
        position:'absolute',left:`${BIKER_X-BIKER_W/2}px`,top:`${bikerTop}px`,
        width:`${BIKER_W}px`,height:`${BIKER_H}px`,
        transform:`translateY(${bikerY}px) rotate(${bikerTilt}deg)`,
        transformOrigin:'50% 100%',
        transition:bikerTilt>0?'transform 0.3s ease-out':'none',
        animation:crashed?'irShake 0.4s ease-in-out':'none',
        '--tilt':`${bikerTilt}deg`,zIndex:5,pointerEvents:'none',
      } as any}/>

      <div ref={obsRef} style={{
        display:obsVisible?'block':'none',
        position:'absolute',
        left:`${obsX-OBS_W/2}px`,top:`${ROAD_MID_Y-OBS_H/2}px`,
        width:`${OBS_W}px`,height:`${OBS_H}px`,
        transform:'scaleX(-1)',zIndex:6,pointerEvents:'none',
      }}/>

      {potholeNear&&phase==='riding'&&(
        <div style={{position:'absolute',top:48,left:'50%',zIndex:20,
          animation:'irDanger .3s ease-in-out infinite',
          background:'rgba(239,68,68,.92)',border:'1px solid rgba(255,255,255,.3)',
          borderRadius:20,padding:'3px 18px',fontSize:12,fontWeight:800,color:'#fff',whiteSpace:'nowrap'}}>
          ⚠️ JUMP NOW!
        </div>
      )}

      <div style={{position:'absolute',top:0,left:0,right:0,zIndex:10,
        display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'7px 14px',background:'#0a1628',borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:18}}>🏍️</span>
          <strong style={{fontSize:13,letterSpacing:.5}}>INDIAN ROAD</strong>
          <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:4,
            background:phase==='riding'?'rgba(34,197,94,.2)':phase==='cooked'?'rgba(239,68,68,.2)':phase==='finished'?'rgba(201,168,76,.2)':'rgba(34,197,94,.15)',
            color:phase==='riding'?'#22c55e':phase==='cooked'?'#ef4444':phase==='finished'?'#c9a84c':'#22c55e'}}>
            {phase==='idle'?'Ready':phase==='starting'?'Starting...':phase==='riding'?'Riding 🏍️':phase==='cooked'?'Crashed! 💥':'Won! 🏆'}
          </span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          {phase==='riding'&&step>0&&(
            <span style={{fontSize:11,color:'#475569'}}>{step} hop{step>1?'s':''} · {multiplier.toFixed(2)}×</span>
          )}
          <SoundToggleBtn/>
          <div style={{background:'rgba(201,168,76,.1)',border:'1px solid rgba(201,168,76,.25)',
            borderRadius:8,padding:'4px 10px',fontWeight:700,color:'#c9a84c',fontSize:13}}>
            {balance!==null?`₹${fmtCoins(balance)}`:'—'}
          </div>
        </div>
      </div>

      {phase==='riding'&&step>0&&(
        <div style={{position:'absolute',top:58,left:'50%',transform:'translateX(-50%)',
          zIndex:10,background:'rgba(0,0,0,.65)',border:'1px solid rgba(251,191,36,.5)',
          borderRadius:20,padding:'3px 14px',fontSize:11,fontWeight:700,color:'#fbbf24',whiteSpace:'nowrap'}}>
          {step} hop{step>1?'s':''} · {multiplier.toFixed(2)}× · ₹{cashoutVal.toFixed(0)}
        </div>
      )}

      {toast&&(
        <div style={{position:'absolute',top:52,left:'50%',zIndex:30,
          animation:'irToast 2.5s ease-in-out forwards',
          background:'rgba(0,0,0,.85)',color:'#fff',padding:'7px 20px',
          borderRadius:20,fontSize:14,fontWeight:700,whiteSpace:'nowrap'}}>
          {toast}
        </div>
      )}

      <div style={{position:'absolute',bottom:0,left:0,right:0,zIndex:10,
        background:'linear-gradient(to top,rgba(0,0,0,.96),rgba(0,0,0,.65))',
        backdropFilter:'blur(6px)',padding:'8px 12px 14px'}}>

        {(phase==='idle'||phase==='starting')&&(<>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:5,marginBottom:8}}>
            {Object.keys(DIFF).map(d=>(
              <button key={d} className="ir-diff" onClick={()=>setDifficulty(d)}
                style={{padding:'6px 2px',borderRadius:6,fontSize:11,fontWeight:700,lineHeight:1.4,cursor:'pointer',
                  border:difficulty===d?`2px solid ${DIFF[d].color}`:'2px solid rgba(255,255,255,.15)',
                  background:difficulty===d?`${DIFF[d].color}22`:'rgba(255,255,255,.06)',
                  color:difficulty===d?DIFF[d].color:'rgba(255,255,255,.45)'}}>
                {DIFF[d].label}<br/><span style={{fontSize:9,fontWeight:400}}>{DIFF[d].survival} safe</span>
              </button>
            ))}
          </div>
          <div style={{display:'flex',gap:5,justifyContent:'center',marginBottom:7}}>
            {AMOUNTS.map(a=>(
              <button key={a} className="ir-chip" onClick={()=>setStake(a)}
                style={{borderRadius:'50%',width:38,height:38,fontWeight:800,fontSize:11,
                  border:stake===a?'2.5px solid #fff':'2px solid rgba(255,255,255,.22)',
                  background:stake===a?'linear-gradient(135deg,#f59e0b,#d97706)':'rgba(255,255,255,.09)',
                  color:stake===a?'#fff':'rgba(255,255,255,.5)'}}>
                {a>=1000?`${a/1000}K`:a}
              </button>
            ))}
          </div>
          <button onClick={()=>{
            const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
            if(!s||phase!=='idle'||!assetsReady) return
            if(balance!==null&&balance<stake) return showToast('Not enough balance')
            setPhase('starting')
            s.emit('chicken:start',{player_id:playerId,operator_id:operatorId,difficulty,stake})
          }} disabled={phase==='starting'||!assetsReady||(balance!==null&&balance<stake)}
            style={{width:'100%',padding:'11px 0',borderRadius:8,border:'none',
              fontWeight:800,fontSize:15,cursor:phase==='starting'?'default':'pointer',
              background:phase==='starting'?'rgba(255,255,255,.07)':'linear-gradient(135deg,#f59e0b,#d97706)',
              color:phase==='starting'?'rgba(255,255,255,.3)':'#fff'}}>
            {phase==='starting'?'⏳ Starting...':`🏍️ START RIDE — ₹${stake}`}
          </button>
          <div style={{textAlign:'center',marginTop:5,fontSize:10,color:'rgba(255,255,255,.25)'}}>
            Space / ↑ arrow to jump
          </div>
        </>)}

        {phase==='riding'&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            <button onClick={handleJump} disabled={jumpCooldown}
              className={potholeNear&&!jumpCooldown?'ir-jump':''}
              style={{padding:'14px 0',borderRadius:8,fontWeight:800,fontSize:18,
                cursor:jumpCooldown?'default':'pointer',
                border:potholeNear?'2px solid rgba(255,255,255,.4)':'none',
                background:potholeNear&&!jumpCooldown?'linear-gradient(135deg,#ef4444,#b91c1c)':'rgba(239,68,68,.2)',
                color:jumpCooldown?'rgba(255,255,255,.2)':'#fff'}}>
              {jumpCooldown?'⬆️':'🦘 JUMP'}
            </button>
            <button onClick={handleCashout} disabled={step===0}
              style={{padding:'14px 0',borderRadius:8,border:'none',
                cursor:step>0?'pointer':'default',fontWeight:800,fontSize:13,lineHeight:1.3,
                background:step>0?'linear-gradient(135deg,#22c55e,#15803d)':'rgba(255,255,255,.06)',
                color:step>0?'#fff':'rgba(255,255,255,.18)'}}>
              💰 CASH OUT<br/>
              <span style={{fontSize:11,fontWeight:400}}>{step>0?`₹${cashoutVal.toFixed(0)}`:'hop first'}</span>
            </button>
          </div>
        )}

        {(phase==='cooked'||phase==='finished')&&(
          <div style={{textAlign:'center',padding:'10px 0',fontSize:13,fontWeight:700,
            color:phase==='cooked'?'#ef4444':'#22c55e'}}>
            {phase==='cooked'?'💥 Crashed! Restarting...':`🏆 Cashed out ₹${cashoutVal.toFixed(0)}! Next ride...`}
          </div>
        )}
      </div>
    </div>
  )
}
